import { useEffect, useState } from "react";
import { ApiValidationError } from "./api";
import { ConditionSection } from "./ConditionSection";
import { ParamsEditor } from "./ParamsEditor";
import { normaliseParams, paramsChanged } from "./params";
import type { ActionRule, EditorMetadata, TransitionAction } from "./types";
import type { EditorMutations } from "./useEditorMutations";

/**
 * One rule: its condition, its parameters, and the controls to move or remove it.
 *
 * Parameters are a local draft with an explicit Save. That is what makes the unsaved-changes
 * guard meaningful — a keystroke-by-keystroke autosave would have nothing to protect, and would
 * write invalid intermediate states to the server.
 */
export function RuleEditor({
    rule,
    index,
    ruleCount,
    config,
    metadata,
    mutations,
    onDirtyChange,
    onRequestDelete,
}: {
    rule: ActionRule;
    index: number;
    ruleCount: number;
    config: TransitionAction;
    metadata: EditorMetadata;
    mutations: EditorMutations;
    onDirtyChange: (key: string, dirty: boolean) => void;
    onRequestDelete: () => void;
}) {
    const [draft, setDraft] = useState<Record<string, unknown>>(
        () => rule.extraParams ?? {},
    );

    const stored = rule.extraParams ?? {};
    const isDirty = paramsChanged(config.paramSpec, stored, draft);

    // The refetch after any write returns a new rule object; adopting the stored values keeps the
    // draft from showing what the server has already rejected or superseded.
    useEffect(() => {
        setDraft(rule.extraParams ?? {});
    }, [rule.extraParams]);

    const dirtyKey = `rule:${rule.id}`;
    useEffect(() => {
        onDirtyChange(dirtyKey, isDirty);
        return () => onDirtyChange(dirtyKey, false);
    }, [dirtyKey, isDirty, onDirtyChange]);

    const save = mutations.saveRuleParams;
    const fieldErrors =
        save.error instanceof ApiValidationError &&
        save.variables?.id === rule.id
            ? save.error.fieldErrors
            : {};

    const isFirst = index === 0;
    const isLast = index === ruleCount - 1;

    /** Move this rule one place, sending the whole new order — the server assigns positions. */
    const move = (offset: number) => {
        const ids = config.rules.map((item) => item.id);
        const target = index + offset;
        [ids[index], ids[target]] = [ids[target], ids[index]];
        mutations.moveRules.mutate({
            transitionActionId: config.id,
            ruleIds: ids,
        });
    };

    return (
        <li className="aer-wf-rule">
            <div className="aer-wf-rule__header">
                <span className="aer-wf-rule__ordinal">Rule {index + 1}</span>
                {rule.condition ? (
                    <span className="aer-wf-rule__kind">if</span>
                ) : (
                    <span className="aer-wf-rule__kind aer-wf-rule__kind--always">
                        always matches
                    </span>
                )}

                <div className="aer-wf-rule__controls">
                    <button
                        type="button"
                        className="aer-wf-icon-button"
                        aria-label={`Move rule ${index + 1} earlier`}
                        disabled={isFirst || mutations.moveRules.isLoading}
                        onClick={() => move(-1)}
                    >
                        ↑
                    </button>
                    <button
                        type="button"
                        className="aer-wf-icon-button"
                        aria-label={`Move rule ${index + 1} later`}
                        disabled={isLast || mutations.moveRules.isLoading}
                        onClick={() => move(1)}
                    >
                        ↓
                    </button>
                    <button
                        type="button"
                        className="aer-wf-icon-button aer-wf-icon-button--danger"
                        aria-label={`Delete rule ${index + 1}`}
                        onClick={onRequestDelete}
                    >
                        ✕
                    </button>
                </div>
            </div>

            {!rule.condition && index < ruleCount - 1 && (
                <p className="aer-wf-field__hint aer-wf-field__hint--warn">
                    An unconditional rule matches everything, so no rule below
                    it can ever be reached.
                </p>
            )}

            <ConditionSection
                condition={rule.condition}
                attachTo={{ kind: "rule", id: rule.id }}
                metadata={metadata}
                mutations={mutations}
                dirtyKey={`rule-condition:${rule.id}`}
                onDirtyChange={onDirtyChange}
                absentMeaning="No condition — this rule matches every event."
            />

            <ParamsEditor
                specs={config.paramSpec}
                values={draft}
                errors={fieldErrors}
                disabled={save.isLoading}
                onChange={(name, value) =>
                    setDraft((current) => ({ ...current, [name]: value }))
                }
            />

            {config.paramSpec.length > 0 && (
                <div className="aer-wf-rule__save">
                    <button
                        type="button"
                        className="aer-wf-button"
                        disabled={!isDirty || save.isLoading}
                        onClick={() =>
                            save.mutate({
                                id: rule.id,
                                extraParams: normaliseParams(
                                    config.paramSpec,
                                    draft,
                                ),
                            })
                        }
                    >
                        {save.isLoading && save.variables?.id === rule.id
                            ? "Saving…"
                            : "Save parameters"}
                    </button>
                    {isDirty && (
                        <button
                            type="button"
                            className="aer-wf-button aer-wf-button--secondary"
                            onClick={() => setDraft(stored)}
                        >
                            Discard
                        </button>
                    )}
                    {isDirty && (
                        <span className="aer-wf-rule__dirty">Unsaved</span>
                    )}
                </div>
            )}
        </li>
    );
}
